<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { claimTaxYearConfig } from '@/composables/useClaimTaxBack'

/**
 * Common questions for the claim tax back area. Native disclosure elements
 * keep this keyboard and screen-reader friendly without extra wiring.
 * IsTaxAdvice leads so the "not personal advice" limit is easy to find.
 * WhyUpTo sits next so the "up to" estimate and HMRC calculation are clear.
 * The payment answer deliberately promises nothing about amounts or timing.
 */
const { t } = useI18n()

const yearLabel = claimTaxYearConfig.yearLabel

const FAQ_KEYS = [
  'IsTaxAdvice',
  'WhyUpTo',
  'HowPaid',
  'WhyNetAmount',
  'KeepRecords',
  'CharityImpact',
  'EarlierYears',
] as const

/** Split FAQ answers on blank lines so legal copy can use more than one paragraph. */
function answerParagraphs(key: (typeof FAQ_KEYS)[number]): string[] {
  return t(`Dashboard.ClaimTaxBack.Guide.Faq.${key}.A`, { year: yearLabel })
    .split(/\n\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0)
}
</script>

<template>
  <section
    class="sa-card sa-card--padded sa-claim-card"
    aria-labelledby="claim-guide-faq-heading"
  >
    <h2 id="claim-guide-faq-heading" class="sa-claim-guide__step-title">
      {{ t('Dashboard.ClaimTaxBack.Guide.Faq.Heading') }}
    </h2>
    <details v-for="key in FAQ_KEYS" :key="key" class="sa-claim-details">
      <summary class="sa-claim-details__summary">
        {{ t(`Dashboard.ClaimTaxBack.Guide.Faq.${key}.Q`) }}
      </summary>
      <p
        v-for="(paragraph, index) in answerParagraphs(key)"
        :key="index"
        class="sa-claim-details__body"
      >
        {{ paragraph }}
      </p>
    </details>
  </section>
</template>
